import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, Download, ExternalLink, Search } from "lucide-react";
import { format } from "date-fns";

const ContractLaborDocuments = () => {
  const { user } = useAuth();
  const [search, setSearch] = useState("");

  // Fetch WO documents for assigned work orders
  const { data: documents = [], isLoading } = useQuery({
    queryKey: ["cl_documents", user?.id],
    queryFn: async () => {
      // Get documents from work_order_documents for assigned WOs
      const { data, error } = await (supabase
        .from("work_order_documents" as any) as any)
        .select("id, file_name, file_url, document_type, created_at, work_order_id, work_orders(number, projects(name))")
        .order("created_at", { ascending: false });
      if (error) {
        // Table may not exist yet — return empty
        console.warn("work_order_documents:", error.message);
        return [];
      }
      return data ?? [];
    },
    enabled: !!user,
  });

  const filtered = search.trim()
    ? documents.filter((d: any) =>
        (d.file_name ?? "").toLowerCase().includes(search.toLowerCase()) ||
        (d.document_type ?? "").toLowerCase().includes(search.toLowerCase()) ||
        (d.work_orders?.number ?? "").toLowerCase().includes(search.toLowerCase())
      )
    : documents;

  if (isLoading) return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16" />)}
    </div>
  );

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold">My Documents</h1>
      <p className="text-sm text-muted-foreground">
        Documents related to your assigned work orders. No financial documents are shown here.
      </p>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search documents…"
          className="pl-8"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p>{documents.length === 0 ? "No documents available." : "No documents match your search."}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((doc: any) => (
            <div key={doc.id} className="border rounded-lg p-3 flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <FileText className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{doc.file_name ?? "Document"}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {doc.document_type && (
                      <Badge variant="outline" className="text-[10px] capitalize">
                        {doc.document_type.replace("_", " ")}
                      </Badge>
                    )}
                    {doc.work_orders && (
                      <span className="text-xs text-muted-foreground font-mono">{doc.work_orders.number}</span>
                    )}
                    {doc.work_orders?.projects && (
                      <span className="text-xs text-muted-foreground">{doc.work_orders.projects.name}</span>
                    )}
                  </div>
                  {doc.created_at && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {format(new Date(doc.created_at), "MMM d, yyyy")}
                    </p>
                  )}
                </div>
              </div>
              {doc.file_url && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    asChild
                  >
                    <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContractLaborDocuments;
